import { IconButton, MenuItem } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import usePopover from '../../../../../../../../hooks/usePopover';
import { useTrans } from '../../../../../../../../services/i18n';
import {
  BottomLeftPosition,
  ContextMenuListItem,
  Popover,
} from '../../../../../../../../components/common/Dialogs/Popover';
import Typography from '../../../../../../../../components/common/Typography';
import connect from '../../../../../connect';
import { ReactComponent as FilterIcon } from '../../.././../../icons/ico-filter-alt.svg';
import { ReactComponent as ResetIcon } from '../../.././../../icons/ico-reset.svg';
import { ReactComponent as CloseIcon } from '../../.././../../icons/ico-close-popup.svg';

import { Divider } from '../../../../../../../../components/common/Divider';

import { SelectField } from '../../../../../../../../components/common/Inputs';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { TagsAutocomplete } from '../../../../../../../../components/autocompletes/TagsAutocomplete';
import { NaicsCodeAutocomplete } from '../../../../../../../../components/autocompletes/NaicsCodeAutocomplete';
import { SetAsideAutocomplete } from '../../../../../../../../components/autocompletes/SetAsideAutocomplete';
import { LocationsAutocomplete } from '../../../../../../../../components/autocompletes/LocationsAutocomplete';
import { emptyArray } from '../../../../../../../../utils/arrayUtils';
import {
  buildLocationFilterValue,
  buildNaicsFilterValue,
  buildSetAsideFilterValue,
  buildTagFilterValue,
  createUpdatedListFilters,
  hasQuickFiltersActive,
  syncExpirationStatus,
  syncLocationSelection,
  syncTagSelection,
} from './filterHelpers';

const useStyles = makeStyles((theme) => ({
  labelDropdowns: {
    fontFamily: 'Lexend',
    marginBottom: '8px',
    color: '#4e5978',
    fontWeight: 500,
  },
  input: {
    padding: '8px 18px',
  },
  filterActive: {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(36, 69, 167, 0.08)',
  },
}));

const EXPIRATION_STATUSES = [
  { name: 'All', value: 'all' },
  { name: 'Expired Soon', value: 'soonExpired' },
  { name: 'Expired', value: 'expired' },
];

const Filters = ({ vendorsTags, filters, getList, changeFilters }) => {
  const styles = useStyles();

  const [anchorEl, onOpenPopover, onClosePopover] = usePopover();
  const { trans } = useTrans();

  const [tags, setTags] = useState([{ create: 'All', value: 'all' }]);
  const [setAsides, setSetAsides] = useState([]);
  const [naics, setNaics] = useState([]);
  const [locations, setLocations] = useState([]);

  const [expirationStatus, setExpirationStatus] = useState(EXPIRATION_STATUSES[0]);
  const [tag, setTag] = useState([]);

  const { filters: activeFilterMap, type: filterType } = filters;

  const applyFilters = (updater) => {
    const next = createUpdatedListFilters(filters, updater);
    changeFilters(next);
    getList(next);
  };

  const changeExpirationStatus = (ev) => {
    setExpirationStatus(ev.target.value);
    applyFilters((next) => {
      next.type = ev.target.value.value;
    });
  };

  function changeTag(ev) {
    if (ev.target.value.some((i) => i.value === 'all')) {
      setTag(() => tags.filter((t) => t.value === 'all'));
    } else {
      setTag(() => ev.target.value);
    }
    filterTags(ev.target.value);
  }

  const filterTags = (val) => {
    applyFilters((next) => {
      const payload = buildTagFilterValue(val);
      if (payload) {
        next.filters.tags = payload;
      } else {
        delete next.filters.tags;
      }
    });
  };

  const changeLocations = (ev) => {
    const val = ev.target.value;
    setLocations(ev.target.value);
    applyFilters((next) => {
      const payload = buildLocationFilterValue(val);
      if (payload) {
        next.filters.state = payload;
      } else {
        delete next.filters.state;
      }
    });
  };

  const changeSetAsides = (ev) => {
    const val = ev.target.value;
    setSetAsides(ev.target.value);
    applyFilters((next) => {
      const payload = buildSetAsideFilterValue(val);
      if (payload) {
        next.filters.setAsides = payload;
      } else {
        delete next.filters.setAsides;
      }
    });
  };

  const changeNaics = (ev) => {
    const val = ev.target.value;
    setNaics(ev.target.value);
    applyFilters((next) => {
      const payload = buildNaicsFilterValue(val);
      if (payload) {
        next.filters.naics = payload;
      } else {
        delete next.filters.naics;
      }
    });
  };

  const resetFilters = () => {
    setTag([]);
    setSetAsides([]);
    setNaics([]);
    setLocations([]);
    setExpirationStatus(EXPIRATION_STATUSES[0]);
    applyFilters((next) => {
      next.type = 'all';
      next.filters = {};
    });
  };

  const hasActiveFilters = hasQuickFiltersActive(filterType, activeFilterMap);

  useEffect(() => {
    setTags([{ create: 'all', value: 'all' }, ...vendorsTags]);
  }, [vendorsTags]);

  useEffect(() => {
    setExpirationStatus(syncExpirationStatus(filterType, EXPIRATION_STATUSES));
    setTag(syncTagSelection(activeFilterMap.tags?.value));
    setNaics(emptyArray(activeFilterMap.naics?.value) ? [] : activeFilterMap.naics.value);
    setSetAsides(emptyArray(activeFilterMap.setAsides?.value) ? [] : activeFilterMap.setAsides.value);
    setLocations(syncLocationSelection(activeFilterMap.state?.value));
  }, [filterType, activeFilterMap]);

  return (
    <>
      <IconButton onClick={onOpenPopover} className={hasActiveFilters ? styles.filterActive : undefined}>
        <FilterIcon />
      </IconButton>

      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClosePopover} {...BottomLeftPosition}>
        <ContextMenuListItem
          size="normal"
          style={{ width: '400px', padding: '16px 16px', display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" weight="600" noWrap>
            {trans('quick-filters')}
          </Typography>

          <div>
            <ResetIcon onClick={resetFilters} style={{ marginRight: '32px', cursor: 'pointer' }} />{' '}
            <CloseIcon onClick={() => onClosePopover()} style={{ cursor: 'pointer' }} />
          </div>
        </ContextMenuListItem>

        <Divider light />

        <div className={styles.input} style={{ marginTop: '15px' }}>
          <div className={styles.labelDropdowns}>{trans('expiration-status')}</div>
          <SelectField
            fullWidth
            value={expirationStatus}
            onChange={changeExpirationStatus}
            renderValue={(item) => item && `${item.name}`}
            type="select"
          >
            {EXPIRATION_STATUSES.map((item, ind) => (
              <MenuItem key={ind} value={item}>
                {item.name}
              </MenuItem>
            ))}
          </SelectField>
        </div>

        <div className={styles.input}>
          <SetAsideAutocomplete
            label={trans('set-asides')}
            placeholder={trans('typeToSearchSetAsides')}
            multi
            value={setAsides}
            onChange={changeSetAsides}
          />
        </div>

        <div className={styles.input}>
          <NaicsCodeAutocomplete
            label={trans('naics-code')}
            placeholder={trans('typeToSearchNaics')}
            multi
            value={naics}
            onChange={changeNaics}
          />
        </div>

        <div className={styles.input}>
          <LocationsAutocomplete
            label={trans('locations')}
            labelKey="name"
            placeholder={trans('typeToSearchLocation')}
            multi
            value={locations}
            onChange={changeLocations}
          />
        </div>

        <div className={styles.input}>
          <TagsAutocomplete
            label={trans('tag')}
            required
            placeholder={trans('tag-name')}
            multi
            freeSolo={false}
            options={tags}
            value={tag}
            onChange={changeTag}
          />
        </div>
      </Popover>
    </>
  );
};

const filterFieldShape = PropTypes.shape({
  type: PropTypes.string,
  value: PropTypes.array,
});

Filters.propTypes = {
  vendorsTags: PropTypes.arrayOf(
    PropTypes.shape({
      create: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  filters: PropTypes.shape({
    type: PropTypes.string,
    pagination: PropTypes.shape({
      page: PropTypes.number,
      perPage: PropTypes.number,
    }).isRequired,
    filters: PropTypes.shape({
      tags: filterFieldShape,
      naics: filterFieldShape,
      setAsides: filterFieldShape,
      state: filterFieldShape,
    }).isRequired,
  }).isRequired,
  getList: PropTypes.func.isRequired,
  changeFilters: PropTypes.func.isRequired,
};

export default connect(Filters);
